import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Plus, Edit, Trash2, ChevronDown, ChevronRight, Move, Layers } from "lucide-react";
import apiWithAuth from "../../services/apiWithAuth";
import { usePermissions } from "../../hooks/usePermissions";

/**
 * Service category admin — 3-level hierarchy (L1 parent → L2 category → L3 sub),
 * leaf / basePrice fields, reorder within siblings, cascade deactivate.
 */

const DEFAULT_PAGE_LIMIT = 10;
const ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 50, 100];

function toShortDate(d) {
    try {
        return new Date(d).toLocaleDateString();
    } catch (e) {
        return "-";
    }
}

function parentKeyFromItem(item) {
    if (!item || item.parentId == null || item.parentId === "") return null;
    const p = item.parentId;
    if (typeof p === "object" && p._id != null) return String(p._id);
    return String(p);
}

function buildIdMap(flatArr) {
    const m = new Map();
    (flatArr || []).forEach((it) => m.set(String(it._id), it));
    return m;
}

/** Effective level 1..3 from stored `level` or depth from root. */
function inferLevel(item, idMap) {
    const lev = Number(item?.level);
    if (Number.isFinite(lev) && lev >= 1 && lev <= 3) return lev;
    let depth = 1;
    let cur = item;
    let guard = 0;
    while (parentKeyFromItem(cur) && guard++ < 64) {
        depth += 1;
        const p = idMap.get(parentKeyFromItem(cur));
        if (!p) break;
        cur = p;
    }
    return Math.min(3, depth);
}

function findRootCategoryId(flatArr, itemId) {
    const idMap = buildIdMap(flatArr);
    let cur = idMap.get(String(itemId));
    if (!cur) return null;
    let guard = 0;
    while (parentKeyFromItem(cur) != null && guard++ < 64) {
        const pk = parentKeyFromItem(cur);
        const p = idMap.get(pk);
        if (!p) break;
        cur = p;
    }
    return cur._id;
}

/** All descendant ids (not including rootId). */
function collectDescendantIds(rootId, flatArr) {
    const out = [];
    const q = [String(rootId)];
    const seen = new Set();
    while (q.length) {
        const pid = q.shift();
        flatArr.forEach((x) => {
            if (parentKeyFromItem(x) === pid) {
                const id = String(x._id);
                if (!seen.has(id)) {
                    seen.add(id);
                    out.push(id);
                    q.push(id);
                }
            }
        });
    }
    return out;
}

export default function ServiceCategoryManagement() {
    const permissions = usePermissions("Service Categories");
    const [flat, setFlat] = useState([]);
    const [roots, setRoots] = useState([]);
    const [paginatedRoots, setPaginatedRoots] = useState([]);
    const [tree, setTree] = useState([]);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(DEFAULT_PAGE_LIMIT);
    const [totalRoots, setTotalRoots] = useState(0);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [openMap, setOpenMap] = useState({});
    const [showAddEditModal, setShowAddEditModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        name: "",
        description: "",
        parentId: null,
        imageUrl: "",
        isActive: true,
        level: 1,
        isLeaf: false,
        basePrice: "",
    });
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");
    const [reorderMode, setReorderMode] = useState(false);
    const [reorderingSaving, setReorderingSaving] = useState(false);
    const dragSourceIdRef = useRef(null);

    const idMap = useMemo(() => buildIdMap(flat), [flat]);

    const fetchList = async () => {
        setLoading(true);
        setError("");
        try {
            const url = `/admin-panel/service-category?limit=10000`;
            const res = await apiWithAuth.get(url);
            if (!res.data?.success) throw new Error(res.data?.message || "Failed");

            const items = res.data.data || [];
            setFlat(items);

            const rootItems = items.filter((item) => parentKeyFromItem(item) == null);
            setRoots(rootItems);
            setTotalRoots(rootItems.length);

            applyPaginationAndBuildTree(rootItems, page, limit, items);
        } catch (err) {
            console.error(err);
            setError(err?.response?.data?.message || err.message || "Failed to fetch categories");
        } finally {
            setLoading(false);
        }
    };

    function buildTree(arr, currentRoots) {
        const map = {};
        arr.forEach((it) => {
            map[String(it._id)] = { ...it, children: [] };
        });

        arr.forEach((it) => {
            const pk = parentKeyFromItem(it);
            const idKey = String(it._id);
            if (pk != null && map[pk] && map[idKey]) {
                map[pk].children.push(map[idKey]);
            }
        });

        const treeNodes = currentRoots.map((root) => map[String(root._id)] || root);

        const sortFn = (a, b) => {
            const aRank = a.rank || 0;
            const bRank = b.rank || 0;
            if (aRank !== bRank) return aRank - bRank;
            const aName = a.name || "";
            const bName = b.name || "";
            return aName.localeCompare(bName);
        };

        function sortRec(nodes) {
            nodes.sort(sortFn);
            nodes.forEach((n) => n.children && sortRec(n.children));
        }
        sortRec(treeNodes);
        return treeNodes;
    }

    const applyPaginationAndBuildTree = (allRoots, currentPage, currentLimit, flatOverride = flat) => {
        const startIndex = (currentPage - 1) * currentLimit;
        const endIndex = startIndex + currentLimit;
        const currentRootsPage = allRoots.slice(startIndex, endIndex);
        setPaginatedRoots(currentRootsPage);
        const t = buildTree(flatOverride, currentRootsPage);
        setTree(t);
    };

    useEffect(() => {
        fetchList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (reorderMode) {
            const allRoots = flat.filter((item) => parentKeyFromItem(item) == null);
            setTree(buildTree(flat, allRoots));
            return;
        }
        applyPaginationAndBuildTree(roots, page, limit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, limit, roots, flat, reorderMode]);

    const toggleOpen = (id) => {
        setOpenMap((m) => ({ ...m, [id]: !m[id] }));
    };

    const computeCreateLevel = (parentId) => {
        if (!parentId) return 1;
        const p = idMap.get(String(parentId));
        if (!p) return 2;
        return Math.min(3, inferLevel(p, idMap) + 1);
    };

    const openCreate = (parentId = null) => {
        setEditing(null);
        setError("");
        const level = computeCreateLevel(parentId);
        if (parentId && level > 3) {
            setError("Maximum depth is 3 levels (parent → category → sub-category).");
            return;
        }
        setForm({
            name: "",
            description: "",
            parentId: parentId || null,
            imageUrl: "",
            isActive: true,
            level,
            isLeaf: level === 3,
            basePrice: "",
        });
        setShowAddEditModal(true);
    };

    const openEdit = (cat) => {
        setEditing(cat);
        setError("");
        const lv = inferLevel(cat, idMap);
        setForm({
            name: cat.name || "",
            description: cat.description || "",
            parentId: cat.parentId || null,
            imageUrl: cat.imageUrl || "",
            isActive: !!cat.isActive,
            level: lv,
            isLeaf: !!cat.isLeaf,
            basePrice: cat.basePrice != null && cat.basePrice !== "" ? String(cat.basePrice) : "",
        });
        setShowAddEditModal(true);
    };

    const payloadBasePrice = () => {
        const leafish = form.level === 3 || form.isLeaf;
        if (!leafish) return null;
        if (form.basePrice === "" || form.basePrice == null) return null;
        const n = Number(form.basePrice);
        return Number.isFinite(n) ? n : null;
    };

    const submitCreate = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.name || form.name.trim() === "") return setError("Name required");
        if (form.parentId) {
            const p = idMap.get(String(form.parentId));
            if (p && inferLevel(p, idMap) >= 3) {
                return setError("Cannot add a child under a level-3 category.");
            }
        }
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description,
                parentId: form.parentId || null,
                imageUrl: form.imageUrl,
                isActive: form.isActive,
                level: form.level,
                isLeaf: form.level === 3 ? true : !!form.isLeaf,
                basePrice: payloadBasePrice(),
            };
            const res = await apiWithAuth.post("/admin-panel/service-category", payload);
            if (!res.data?.success) throw new Error(res.data?.message || "Create failed");
            setSuccess("Category created");
            setShowAddEditModal(false);
            fetchList();
            setPage(1);
        } catch (err) {
            console.error(err);
            setError(err?.response?.data?.message || err.message || "Create failed");
        }
    };

    const submitUpdate = async (e) => {
        e.preventDefault();
        setError("");
        if (!editing || !editing._id) return;
        if (!form.name || form.name.trim() === "") return setError("Name required");

        try {
            const url = `/admin-panel/service-category?categoryId=${editing._id}`;
            const lv = inferLevel(editing, idMap);
            const payload = {
                name: form.name.trim(),
                description: form.description,
                imageUrl: form.imageUrl,
                isActive: form.isActive,
                isLeaf: lv === 3 ? true : !!form.isLeaf,
                basePrice: lv === 3 || form.isLeaf ? payloadBasePrice() : null,
            };
            const res = await apiWithAuth.put(url, payload);
            if (!res.data?.success) throw new Error(res.data?.message || "Update failed");

            setSuccess("Category updated");
            setShowAddEditModal(false);
            setEditing(null);
            fetchList();
        } catch (err) {
            console.error(err);
            setError(err?.response?.data?.message || err.message || "Update failed");
        }
    };

    const doDelete = async (catId) => {
        if (!window.confirm("Delete this category? (soft delete)")) return;
        setError("");
        try {
            const res = await apiWithAuth.delete(`/admin-panel/service-category?categoryId=${catId}`);
            if (!res.data?.success) throw new Error(res.data?.message || "Delete failed");

            setSuccess("Category deleted");
            fetchList();
        } catch (err) {
            console.error(err);
            setError(err?.response?.data?.message || err.message || "Delete failed");
        }
    };

    const toggleActive = async (cat) => {
        setError("");
        try {
            const payload = { isActive: !cat.isActive };
            const res = await apiWithAuth.put(`/admin-panel/service-category?categoryId=${cat._id}`, payload);
            if (!res.data?.success) throw new Error(res.data?.message || "Toggle failed");

            setSuccess("Status updated");
            fetchList();
        } catch (err) {
            console.error(err);
            setError(err?.response?.data?.message || err.message || "Toggle failed");
        }
    };

    const deactivateBranch = async (cat) => {
        if (!window.confirm("Set this category and ALL sub-categories to inactive?")) return;
        setError("");
        try {
            const ids = [String(cat._id), ...collectDescendantIds(cat._id, flat)];
            await Promise.all(
                ids.map((id) =>
                    apiWithAuth.put(`/admin-panel/service-category?categoryId=${id}`, { isActive: false })
                )
            );
            setSuccess("Branch deactivated");
            fetchList();
        } catch (err) {
            console.error(err);
            setError(err?.response?.data?.message || err.message || "Cascade deactivate failed");
        }
    };

    const activateBranch = async (cat) => {
        if (!window.confirm("Set this category and ALL sub-categories to active?")) return;
        setError("");
        try {
            const ids = [String(cat._id), ...collectDescendantIds(cat._id, flat)];
            await Promise.all(
                ids.map((id) =>
                    apiWithAuth.put(`/admin-panel/service-category?categoryId=${id}`, { isActive: true })
                )
            );
            setSuccess("Branch activated");
            fetchList();
        } catch (err) {
            console.error(err);
            setError(err?.response?.data?.message || err.message || "Cascade activate failed");
        }
    };

    const getSiblingsOrdered = (flatArr, parentId) => {
        const targetPid = parentId != null && parentId !== "" ? String(parentId) : null;
        const siblings = flatArr.filter((item) => parentKeyFromItem(item) === targetPid);
        siblings.sort((a, b) => {
            const aRank = a.rank || 0;
            const bRank = b.rank || 0;
            if (aRank !== bRank) return aRank - bRank;
            const aName = a.name || "";
            const bName = b.name || "";
            return aName.localeCompare(bName);
        });
        return siblings;
    };

    const saveBulkOrder = async (items) => {
        setError("");
        try {
            const resp = await apiWithAuth.post("/admin-panel/service-category/reorder", { items });
            if (resp.data && resp.data.success) {
                setSuccess("Order saved");
                await fetchList();
            } else {
                throw new Error(resp.data?.message || "Failed to save order");
            }
        } catch (err) {
            console.error("saveBulkOrder error", err);
            setError(err.response?.data?.message || err.message || "Failed to save order");
            await fetchList();
        } finally {
            setReorderingSaving(false);
        }
    };

    const onDragStart = (e, categoryId) => {
        if (!reorderMode) return;
        const id = String(categoryId);
        dragSourceIdRef.current = id;
        e.dataTransfer.effectAllowed = "move";
        try {
            e.dataTransfer.setData("text/plain", id);
        } catch (err) {
            // ignore
        }
    };

    const onDragEnd = () => {
        dragSourceIdRef.current = null;
    };

    const onDragOver = (e) => {
        if (!reorderMode) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
    };

    const onDrop = async (e, overCategoryId, parentIdOfSiblings) => {
        if (!reorderMode) return;
        e.preventDefault();
        e.stopPropagation();

        const fromDataTransfer = e.dataTransfer?.getData("text/plain") || "";
        const sourceId = dragSourceIdRef.current || fromDataTransfer;
        dragSourceIdRef.current = null;
        let destId = String(overCategoryId);
        let siblingParentId = parentIdOfSiblings != null && parentIdOfSiblings !== "" ? parentIdOfSiblings : null;
        if (siblingParentId != null) siblingParentId = String(siblingParentId);

        if (!sourceId || !destId) return;
        if (String(sourceId) === String(destId)) return;

        const sourceItem = flat.find((x) => String(x._id) === String(sourceId));
        const sourceIsRoot = sourceItem && parentKeyFromItem(sourceItem) == null;
        if (sourceIsRoot && siblingParentId != null) {
            const rootId = findRootCategoryId(flat, destId);
            if (rootId != null) {
                destId = String(rootId);
                siblingParentId = null;
            }
        }

        const siblings = getSiblingsOrdered(flat, siblingParentId);
        if (!siblings || siblings.length === 0) return;

        const srcIndex = siblings.findIndex((s) => String(s._id) === String(sourceId));
        const destIndex = siblings.findIndex((s) => String(s._id) === String(destId));
        if (srcIndex === -1 || destIndex === -1) return;

        const newSiblings = [...siblings];
        const [moved] = newSiblings.splice(srcIndex, 1);
        newSiblings.splice(destIndex, 0, moved);

        const changed = [];
        newSiblings.forEach((cat, idx) => {
            const newOrder = idx + 1;
            if ((cat.rank || 0) !== newOrder) changed.push({ id: cat._id, order: newOrder });
        });

        if (changed.length === 0) {
            return;
        }

        setFlat((prev) =>
            prev.map((item) => {
                const pid = parentKeyFromItem(item);
                const targetPid = siblingParentId;
                if (pid !== targetPid) return item;
                const pos = newSiblings.findIndex((s) => String(s._id) === String(item._id));
                if (pos === -1) return item;
                return { ...item, rank: pos + 1 };
            })
        );
        setReorderingSaving(true);

        const items = changed.map((it) => ({
            id: typeof it.id === "object" && it.id != null ? String(it.id) : it.id,
            order: it.order,
            parentId: siblingParentId,
        }));
        await saveBulkOrder(items);
    };

    const toggleReorderMode = () => {
        setReorderMode((prev) => {
            const next = !prev;
            if (!next) {
                fetchList();
            } else {
                setSearch("");
                dragSourceIdRef.current = null;
                setPage(1);
                const rootItems = flat.filter((item) => parentKeyFromItem(item) == null);
                setRoots(rootItems);
                setTotalRoots(rootItems.length);
            }
            return next;
        });
    };

    function CategoryRow({ node, depth = 0 }) {
        const parentId = parentKeyFromItem(node);
        const lv = inferLevel(node, idMap);
        const canAddChild = lv < 3;
        const priceLabel =
            node.isLeaf && node.basePrice != null && node.basePrice !== ""
                ? `₹${node.basePrice}`
                : node.isLeaf
                  ? "Leaf (no base price)"
                  : "";

        return (
            <div
                className="border rounded mb-3 bg-white"
                onDragOver={reorderMode ? onDragOver : undefined}
                onDrop={reorderMode ? (ev) => onDrop(ev, node._id, parentId) : undefined}
            >
                <div
                    className={`flex flex-col gap-3 sm:flex-row sm:items-start p-3 sm:p-4 ${reorderMode ? "bg-yellow-50/30" : ""}`}
                    draggable={reorderMode}
                    onDragStart={(e) => onDragStart(e, node._id)}
                    onDragEnd={onDragEnd}
                >
                    <div className="flex items-start gap-3 flex-1 min-w-0" style={{ marginLeft: depth > 0 ? depth * 10 : undefined }}>
                        <div className="flex items-center gap-0.5 shrink-0">
                        {reorderMode && (
                            <div className="p-1 rounded cursor-grab shrink-0" title="Drag to reorder">
                                <Move size={16} />
                            </div>
                        )}
                        {node.children && node.children.length > 0 ? (
                            <button
                                type="button"
                                onClick={() => toggleOpen(node._id)}
                                className="p-1 rounded hover:bg-gray-100 shrink-0"
                                title="Expand or collapse"
                            >
                                {openMap[node._id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </button>
                        ) : (
                            !reorderMode && <div className="w-6 shrink-0" />
                        )}
                        </div>

                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded bg-gray-50 border flex items-center justify-center overflow-hidden flex-shrink-0">
                            {node.imageUrl ? (
                                <img
                                    src={node.imageUrl}
                                    alt={node.name}
                                    className="w-full h-full object-cover"
                                    draggable={false}
                                />
                            ) : (
                                <div className="text-gray-300 font-bold text-sm sm:text-base">{node.name?.charAt(0)?.toUpperCase() || "?"}</div>
                            )}
                        </div>

                        <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                                <div className="font-semibold uppercase break-words">{node.name}</div>
                                <span className="text-xs px-2 py-0.5 rounded bg-indigo-50 text-indigo-800 border border-indigo-100 flex-shrink-0">
                                    L{lv}
                                </span>
                                {node.isLeaf && (
                                    <span className="text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-100 flex-shrink-0">
                                        Leaf
                                    </span>
                                )}
                                <div
                                    className={`text-xs px-2 py-1 rounded flex-shrink-0 ${
                                        node.isActive ? "text-green-700 bg-green-50" : "text-red-700 bg-red-50"
                                    }`}
                                >
                                    {node.isActive ? "ACTIVE" : "INACTIVE"}
                                </div>
                            </div>

                            <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                                <span className="inline-flex items-center gap-1">
                                    <Layers size={12} />
                                    Order: {node.rank || "-"}
                                </span>
                                <span>Created: {toShortDate(node.createdAt)}</span>
                                {priceLabel && <span className="text-emerald-700 font-medium">{priceLabel}</span>}
                            </div>
                        </div>
                    </div>

                        <div className="flex flex-wrap items-center gap-2 justify-end w-full sm:w-auto flex-shrink-0 pl-9 sm:pl-0">
                            {!reorderMode && permissions.canCreate && canAddChild && (
                                <button
                                    onClick={() => openCreate(node._id)}
                                    className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                                    title="Add child (max depth: L3)"
                                >
                                    <Plus size={14} className="inline mr-1" /> Child
                                </button>
                            )}

                            {!reorderMode && permissions.canUpdate && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => deactivateBranch(node)}
                                        className="px-2 py-1 text-xs bg-orange-50 text-orange-800 rounded border border-orange-200 hover:bg-orange-100"
                                        title="Deactivate this node and all descendants"
                                    >
                                        Branch off
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => activateBranch(node)}
                                        className="px-2 py-1 text-xs bg-green-50 text-green-800 rounded border border-green-200 hover:bg-green-100"
                                        title="Activate this node and all descendants"
                                    >
                                        Branch on
                                    </button>
                                </>
                            )}

                            {!reorderMode && (
                                <label className="inline-flex items-center gap-2" title="Toggle Active Status">
                                    <input type="checkbox" checked={!!node.isActive} onChange={() => toggleActive(node)} />
                                </label>
                            )}

                            {!reorderMode && permissions.canUpdate && (
                                <button
                                    onClick={() => openEdit(node)}
                                    className="p-2 bg-gray-50 rounded hover:bg-gray-100"
                                    title="Edit Category"
                                >
                                    <Edit size={16} />
                                </button>
                            )}

                            {!reorderMode && permissions.canDelete && (
                                <button
                                    onClick={() => doDelete(node._id)}
                                    className="p-2 bg-red-50 rounded hover:bg-red-100"
                                    title="Delete Category"
                                >
                                    <Trash2 size={16} />
                                </button>
                            )}
                        </div>
                </div>

                {node.children && node.children.length > 0 && openMap[node._id] && (
                    <div className="pl-3 sm:pl-6 pr-2 sm:pr-4 pb-3 sm:pb-4">
                        {node.children.map((c) => (
                            <CategoryRow key={c._id} node={c} depth={depth + 1} />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    const handleSearch = async () => {
        setPage(1);

        const searchTerm = search.toLowerCase();
        const filteredRoots = flat.filter(
            (item) => parentKeyFromItem(item) == null && item.name?.toLowerCase().includes(searchTerm)
        );

        setRoots(filteredRoots);
        setTotalRoots(filteredRoots.length);

        applyPaginationAndBuildTree(filteredRoots, 1, limit);
    };

    const totalPages = Math.ceil(Math.max(1, totalRoots) / limit);
    const startRange = totalRoots === 0 ? 0 : (page - 1) * limit + 1;
    const endRange = Math.min(page * limit, totalRoots);

    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value, 10);
        setLimit(newLimit);
        setPage(1);
    };

    const parentNameForForm = () => {
        if (!form.parentId) return null;
        const p = idMap.get(String(form.parentId));
        return p?.name || String(form.parentId);
    };

    const showLeafFields = form.level === 3 || form.isLeaf;
    const showIsLeafToggle = form.level < 3 && !editing;

    return (
        <div className="min-h-screen p-3 sm:p-6 bg-gray-50">
            <div className="max-w-6xl mx-auto">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4 sm:mb-6">
                    <div className="min-w-0">
                        <h1 className="text-xl sm:text-2xl font-bold">Service category management</h1>
                        <p className="text-sm text-gray-600 max-w-2xl mt-1">
                            Three-level tree: <strong>L1</strong> parent (e.g. Home services) → <strong>L2</strong> category
                            (e.g. Electrician) → <strong>L3</strong> sub-category (bookable leaf, e.g. AC repair). L2 can also
                            be a leaf if it has no children. Set <strong>base price</strong> only on leaf nodes.
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto flex-shrink-0">
                        {permissions.canCreate && (
                            <button
                                onClick={() => openCreate(null)}
                                className="flex items-center justify-center gap-2 px-4 py-2 rounded bg-blue-600 text-white text-sm sm:text-base"
                            >
                                <Plus size={16} /> Add L1 (root)
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={toggleReorderMode}
                            className={`flex items-center justify-center gap-2 px-4 py-2 rounded text-sm sm:text-base ${reorderMode ? "bg-yellow-500 text-white" : "bg-gray-100"}`}
                        >
                            <Move size={16} />
                            {reorderMode ? "Disable Reorder" : "Enable Reorder"}
                        </button>
                    </div>
                </div>

                {success && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded">{success}</div>
                )}
                {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">{error}</div>}

                <div className="mb-4">
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                        <input
                            placeholder="Search L1 (root) names..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="border p-2 rounded w-full sm:w-64 bg-white disabled:opacity-50 text-sm"
                            disabled={reorderMode || loading}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleSearch();
                            }}
                        />
                        <button
                            onClick={handleSearch}
                            className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50 text-sm w-full sm:w-auto"
                            disabled={reorderMode || loading}
                        >
                            Search
                        </button>
                    </div>
                </div>

                <div>
                    {reorderingSaving && <div className="mb-3 text-sm text-yellow-700">Saving new order...</div>}
                    {loading ? (
                        <div className="py-12 text-center text-gray-500">Loading categories...</div>
                    ) : tree.length === 0 && totalRoots > 0 ? (
                        <div className="py-8 text-center text-gray-500">No categories found on this page. Go back.</div>
                    ) : tree.length === 0 && totalRoots === 0 ? (
                        <div className="py-8 text-center text-gray-500">No root categories found</div>
                    ) : (
                        tree.map((node) => <CategoryRow key={node._id} node={node} depth={0} />)
                    )}
                </div>

                <div className="bg-white px-3 sm:px-6 py-3 sm:py-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-t border-gray-200 mt-4 sm:mt-6 rounded-b-lg shadow-sm">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 min-w-0">
                        {reorderMode && (
                            <span className="text-sm text-gray-600">
                                Pagination is disabled while reordering (all root categories are shown).
                            </span>
                        )}
                        <div className={`text-sm text-gray-700 flex flex-wrap items-center gap-2 ${reorderMode ? "opacity-50 pointer-events-none" : ""}`}>
                            <span>Rows per page:</span>
                            <select
                                value={limit}
                                onChange={handleLimitChange}
                                className="p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                                disabled={loading || reorderMode}
                            >
                                {ROWS_PER_PAGE_OPTIONS.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <span className={`text-sm text-gray-700 ${reorderMode ? "opacity-50" : ""}`}>
                            {reorderMode
                                ? `Showing all ${totalRoots} root categories`
                                : `Showing ${startRange} to ${endRange} of ${totalRoots} results (L1 roots only)`}
                        </span>
                    </div>

                    <div className={`${reorderMode ? "opacity-50 pointer-events-none" : ""} w-full sm:w-auto`}>
                        <nav className="relative z-0 flex rounded-md shadow-sm w-full sm:w-auto" aria-label="Pagination">
                            <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page <= 1 || loading || reorderMode}
                                className={`flex-1 sm:flex-none relative inline-flex items-center justify-center px-3 sm:px-4 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50
                                    ${page <= 1 ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                                Previous
                            </button>

                            <span className="flex-1 sm:flex-none relative inline-flex items-center justify-center px-3 sm:px-4 py-2 border-t border-b border-gray-300 bg-gray-50 text-sm font-medium text-gray-700 whitespace-nowrap">
                                Page {page} of {totalPages}
                            </span>

                            <button
                                onClick={() => setPage((p) => p + 1)}
                                disabled={page >= totalPages || loading || reorderMode}
                                className={`flex-1 sm:flex-none relative inline-flex items-center justify-center px-3 sm:px-4 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50
                                    ${page >= totalPages ? "opacity-50 cursor-not-allowed" : ""}`}
                            >
                                Next
                            </button>
                        </nav>
                    </div>
                </div>
            </div>

            {showAddEditModal &&
                createPortal(
                <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="p-4 border-b flex items-center justify-between">
                            <div className="text-lg font-semibold">{editing ? "Edit category" : "Add category"}</div>
                            <button
                                onClick={() => {
                                    setShowAddEditModal(false);
                                    setEditing(null);
                                    setError("");
                                }}
                                className="px-3 py-1 rounded bg-gray-100"
                            >
                                Close
                            </button>
                        </div>

                        {error && <div className="m-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">{error}</div>}

                        <form onSubmit={editing ? submitUpdate : submitCreate} className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Name*</label>
                                <input
                                    required
                                    value={form.name}
                                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                    className="mt-1 p-2 border rounded w-full"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Level</label>
                                <input
                                    readOnly
                                    value={`L${form.level} ${form.level === 1 ? "(parent)" : form.level === 2 ? "(category)" : "(sub / leaf tier)"}`}
                                    className="mt-1 p-2 border rounded w-full bg-gray-50"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Parent</label>
                                {editing ? (
                                    <input
                                        readOnly
                                        value={parentNameForForm() || "(root)"}
                                        className="mt-1 p-2 border rounded w-full bg-gray-50"
                                    />
                                ) : form.parentId ? (
                                    <input readOnly value={parentNameForForm() || form.parentId} className="mt-1 p-2 border rounded w-full bg-gray-50" />
                                ) : (
                                    <input readOnly value="— None (L1 root) —" className="mt-1 p-2 border rounded w-full bg-gray-50" />
                                )}
                                {editing && <p className="text-xs text-gray-500 mt-1">Parent cannot be changed when editing.</p>}
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Description</label>
                                <textarea
                                    value={form.description}
                                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                                    className="mt-1 p-2 border rounded w-full"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Image URL</label>
                                <input
                                    value={form.imageUrl}
                                    onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
                                    className="mt-1 p-2 border rounded w-full"
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <label className="text-sm font-medium text-gray-700">Active</label>
                                <input
                                    type="checkbox"
                                    checked={!!form.isActive}
                                    onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                                />
                            </div>

                            {showIsLeafToggle && (
                                <div className="md:col-span-2 flex items-start gap-3 border rounded p-3 bg-slate-50">
                                    <input
                                        type="checkbox"
                                        id="isLeafToggle"
                                        checked={!!form.isLeaf}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                isLeaf: e.target.checked,
                                                basePrice: e.target.checked ? f.basePrice : "",
                                            }))
                                        }
                                    />
                                    <label htmlFor="isLeafToggle" className="text-sm text-gray-700 cursor-pointer">
                                        <strong>Bookable leaf at L2</strong> — check if this L2 category has no L3 children (e.g.
                                        standalone service line). Uncheck if you will add L3 children later.
                                    </label>
                                </div>
                            )}

                            {editing && form.level < 3 && (
                                <div className="md:col-span-2 flex items-start gap-3 border rounded p-3 bg-slate-50">
                                    <input
                                        type="checkbox"
                                        id="isLeafEdit"
                                        checked={!!form.isLeaf}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                isLeaf: e.target.checked,
                                                basePrice: e.target.checked ? f.basePrice : "",
                                            }))
                                        }
                                    />
                                    <label htmlFor="isLeafEdit" className="text-sm text-gray-700 cursor-pointer">
                                        Treat as <strong>leaf</strong> (bookable L2 with no sub-categories). Backend clears{" "}
                                        <code>basePrice</code> when not leaf.
                                    </label>
                                </div>
                            )}

                            {showLeafFields && (
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700">Base price (leaf only)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={form.basePrice}
                                        onChange={(e) => setForm((f) => ({ ...f, basePrice: e.target.value }))}
                                        placeholder="Optional platform default for this leaf"
                                        className="mt-1 p-2 border rounded w-full"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Shop-specific overrides use Shop Service Adoptions API.</p>
                                </div>
                            )}

                            <div className="md:col-span-2 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowAddEditModal(false);
                                        setEditing(null);
                                        setError("");
                                    }}
                                    className="px-4 py-2 rounded bg-gray-100"
                                >
                                    Cancel
                                </button>
                                <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white">
                                    {editing ? "Save" : "Create"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
